import React, { Fragment, useState, useEffect } from 'react';
import { Container, Row, Col, Card, CardBody, Button, Spinner, Alert } from 'reactstrap';
import { Breadcrumbs, H4 } from '../../../../AbstractElements';
import stepApi from '../../../../api/step';
import StepCard from './StepCard';
import StepModal from './StepModal';
import StepStatistics from './StepStatistics';
import StepFilters from './StepFilters';

const StepManagementContain = () => {
  const [steps, setSteps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [statistics, setStatistics] = useState(null);
  
  // Filters
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Modal
  const [modal, setModal] = useState(false);
  const [editingStep, setEditingStep] = useState(null);
  
  // Form data
  const [formData, setFormData] = useState({
    stepName: '',
    stepDescription: '',
    stepOrder: 1,
    stepType: 'quiz',
    stepConfig: {},
    isRequired: true,
    isActive: true
  });

  // Load steps and statistics
  useEffect(() => {
    loadSteps();
    loadStatistics();
  }, [typeFilter, statusFilter]);

  const loadSteps = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = {};
      if (typeFilter !== 'all') params.stepType = typeFilter;
      if (statusFilter !== 'all') params.isActive = statusFilter === 'active';
      
      const response = await stepApi.getAllSteps(params);
      setSteps(response.data?.steps || response.steps || response.data || response);
    } catch (err) {
      console.error('❌ Error loading steps:', err);
      setError('Không thể tải danh sách steps');
      setSteps([]);
    } finally {
      setLoading(false);
    }
  };

  const loadStatistics = async () => {
    try {
      const response = await stepApi.getStepsStatistics();
      setStatistics(response.data?.statistics || response.statistics || response.data);
    } catch (err) {
      console.error('❌ Error loading statistics:', err);
    }
  };

  const toggleModal = () => {
    setModal(!modal);
    if (!modal) {
      setEditingStep(null);
      resetForm();
    }
  };

  const resetForm = () => {
    setFormData({
      stepName: '',
      stepDescription: '',
      stepOrder: 1,
      stepType: 'quiz',
      stepConfig: {},
      isRequired: true,
      isActive: true
    });
  };

  const handleEdit = (step) => {
    setEditingStep(step);
    setFormData({
      stepName: step.stepName,
      stepDescription: step.stepDescription || '',
      stepOrder: step.stepOrder || 1,
      stepType: step.stepType,
      stepConfig: step.stepConfig || {},
      isRequired: step.isRequired !== undefined ? step.isRequired : true,
      isActive: step.isActive
    });
    setModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setError(null);
      
      if (editingStep) {
        await stepApi.updateStep(editingStep.id, formData);
        setSuccess('Cập nhật step thành công!');
      } else {
        await stepApi.createSteps([formData]);
        setSuccess('Tạo step thành công!');
      }
      
      toggleModal();
      loadSteps();
      loadStatistics();
      
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('❌ Error saving step:', err);
      setError('Không thể lưu step');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bạn có chắc chắn muốn xóa step này?')) return;
    
    try {
      await stepApi.deleteStep(id);
      setSuccess('Xóa step thành công!');
      loadSteps();
      loadStatistics();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('❌ Error deleting step:', err);
      setError('Không thể xóa step');
    }
  };

  const handleToggleStatus = async (id) => {
    try {
      await stepApi.toggleStepStatus(id);
      setSuccess('Cập nhật trạng thái thành công!');
      loadSteps();
      loadStatistics();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('❌ Error toggling status:', err);
      setError('Không thể cập nhật trạng thái');
    }
  };

  const handleCreateSampleSteps = async () => {
    if (!window.confirm('Tạo sample steps? (Sẽ tạo các steps mẫu)')) return;
    
    try {
      await stepApi.createStepsWithSample();
      setSuccess('Tạo sample steps thành công!');
      loadSteps();
      loadStatistics();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('❌ Error creating sample steps:', err);
      setError('Không thể tạo sample steps');
    }
  };

  if (loading) {
    return (
      <Fragment>
        <Breadcrumbs parent='Quiz' title='Step Management' mainTitle='Step Management' />
        <Container fluid={true}>
          <div className="text-center py-5">
            <Spinner color="primary" />
            <p className="mt-2 mb-0">Đang tải steps...</p>
          </div>
        </Container>
      </Fragment>
    );
  }

  return (
    <Fragment>
      <Breadcrumbs parent='Quiz' title='Step Management' mainTitle='Quản lý Quiz Steps' />
      <Container fluid={true}>
        {/* Success/Error Messages */}
        {success && <Alert color="success">{success}</Alert>}
        {error && <Alert color="danger">{error}</Alert>}

        {/* Statistics Cards */}
        <StepStatistics statistics={statistics} />

        {/* Header Card */}
        <Card className="shadow-sm border-0 mb-4">
          <CardBody className="p-4">
            <Row className="align-items-center">
              <Col md="6">
                <H4 className="text-dark mb-0">
                  <i className="fa fa-list-ol me-2 text-primary"></i>
                  Quản lý Quiz Steps
                </H4>
              </Col>
              <Col md="6" className="text-end">
                <Button color="success" onClick={handleCreateSampleSteps} className="me-2">
                  <i className="fa fa-magic me-2"></i>
                  Sample Steps
                </Button>
                <Button color="primary" onClick={toggleModal}>
                  <i className="fa fa-plus me-2"></i>
                  Tạo Step Mới
                </Button>
              </Col>
            </Row>

            {/* Filters */}
            <StepFilters 
              typeFilter={typeFilter}
              setTypeFilter={setTypeFilter}
              statusFilter={statusFilter}
              setStatusFilter={setStatusFilter}
            />
          </CardBody>
        </Card>

        {/* Steps List */}
        <Row>
          {steps.length === 0 ? (
            <Col>
              <Card className="text-center py-5">
                <CardBody>
                  <i className="fa fa-list text-muted" style={{fontSize: '3rem'}}></i>
                  <p className="text-muted mt-3">Chưa có step nào</p>
                  <Button color="primary" onClick={handleCreateSampleSteps}>
                    <i className="fa fa-magic me-2"></i>
                    Tạo Sample Steps
                  </Button>
                </CardBody>
              </Card>
            </Col>
          ) : (
            steps.map((step, index) => (
              <Col lg="6" key={step.id || index}>
                <StepCard 
                  step={step}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  onToggleStatus={handleToggleStatus}
                />
              </Col>
            ))
          )}
        </Row>

        {/* Create/Edit Modal */}
        <StepModal 
          isOpen={modal}
          toggle={toggleModal}
          editingStep={editingStep}
          formData={formData}
          setFormData={setFormData}
          onSubmit={handleSubmit}
        />
      </Container>
    </Fragment>
  );
};

export default StepManagementContain;

